import type { OnChangeFn, RowSelectionState } from '@plato/ui/components/data-table';
import { useMemo, useState } from 'react';

export function useRowSelection<T extends Record<K, any>, K extends keyof T>(
    data: T[],
    idKey: K,
    onFiltersChange: (selectedIds: string[]) => void,
    activeFilter: string[],
) {
    const [lastRowSelection, setLastRowSelection] = useState<RowSelectionState>({});

    // MARK: this function is only called when the activeFilters change in the header
    const rowSelection = useMemo(() => {
        // Step 1. For every active filter in the header, we want to find the index of the item in the data array (IF it is there. It might not be visible in the table if it is paginated)

        const indexesWhereTrue = activeFilter.map((id) => {
            const index = data.findIndex((item) => `${item[idKey]}` === id);
            return index;
        });

        // Step 2. For every index, we want to set the rowSelection object to true
        const newSelection: RowSelectionState = {};

        for (const index of indexesWhereTrue) {
            newSelection[index] = true;
        }

        // Step 3. We want to store the current selected ids in the lastSelectedIds state
        // eslint-disable-next-line react-hooks/set-state-in-render -- legacy code
        setLastRowSelection(newSelection);

        // Step 4. We want to return the rowSelection object
        return newSelection;
    }, [activeFilter, data]);

    const setRowSelection: OnChangeFn<RowSelectionState> = (rowSelectionState) => {
        // Step 1. We want to get the new state of the rowSelection object
        const newState =
            typeof rowSelectionState === 'function' ? rowSelectionState(lastRowSelection) : rowSelectionState;

        // Step 2. We want to compare the new state with the old state to determine if an item has been selected or deselected

        const removedSelection: RowSelectionState = {};
        const addedSelection: RowSelectionState = {};

        for (const index of Object.keys(newState)) {
            if (lastRowSelection[index] === undefined) {
                addedSelection[index] = true;
            }
        }

        for (const index of Object.keys(lastRowSelection)) {
            if (newState[index] === undefined) {
                removedSelection[index] = true;
            }
        }

        // Step 3. Now we want to find the value of the added and removed items

        const addedIds = Object.keys(addedSelection).map((index) => {
            const item = data[Number(index)];
            return item ? `${item[idKey]}` : '';
        });

        const removedIds = Object.keys(removedSelection).map((index) => {
            const item = data[Number(index)];
            return item ? `${item[idKey]}` : '';
        });

        // Step 4. Now we take activeFilter - we remove the removedIds and add the addedIds
        const newActiveFilter = [...activeFilter.filter((id) => !removedIds.includes(id)), ...addedIds];

        // Step 5. We want to update the activeFilter
        onFiltersChange(newActiveFilter);
    };

    return { rowSelection, setRowSelection };
}

export default useRowSelection;
